<template>
  <div class="container py-3">

    <!-- Tabs nav -->
    <ul class="nav nav-tabs mb-3" role="tablist">
      <li class="nav-item" v-for="tab in tabs" :key="tab.id">
        <button
          class="nav-link d-flex align-items-center gap-1"
          :class="{ active: tabActivo === tab.id }"
          @click="tabActivo = tab.id"
          type="button"
        >
          <span class="material-symbols-outlined" style="font-size:17px">{{ tab.icono }}</span>
          <span class="small fw-semibold">{{ tab.label }}</span>
        </button>
      </li>
    </ul>

    <!-- Tab: CSV → JSON -->
    <div v-show="tabActivo === 'csv-json'">
      <div class="util-card border-primary">
        <div class="util-card-header bg-primary text-white">
          <span class="material-symbols-outlined">upload_file</span>
          <strong class="small">Cargar CSV y Descargar JSON</strong>
        </div>
        <div class="util-card-body">
          <p class="text-muted small mb-3">
            Carga un archivo CSV con los datos del plano S3 y descarga el resultado en formato JSON.
          </p>
          <div class="mb-3">
            <label class="form-label fw-bold small">Archivo CSV</label>
            <input
              type="file"
              class="form-control form-control-sm"
              ref="fileInput"
              accept=".csv"
              @change="file = $event.target.files[0]"
            />
          </div>
          <button class="btn btn-primary" @click="handleSubmit" :disabled="isLoading || !file">
            <span class="material-symbols-outlined me-1" style="font-size:18px; vertical-align:middle;">
              {{ isLoading ? 'hourglass_top' : 'download' }}
            </span>
            {{ isLoading ? 'Procesando...' : 'Cargar y Descargar JSON' }}
          </button>
          <div v-if="errorMessage" class="alert alert-danger py-1 px-2 small mt-2 mb-0">
            <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">error</span>
            {{ errorMessage }}
          </div>
          <div v-if="successMessage" class="alert alert-success py-1 px-2 small mt-2 mb-0">
            <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">check_circle</span>
            {{ successMessage }}
          </div>
        </div>
      </div>
    </div>

    <!-- Tab: Transformar JSON -->
    <div v-show="tabActivo === 'transform-json'">
      <div class="util-card border-success">
        <div class="util-card-header bg-success text-white">
          <span class="material-symbols-outlined">data_object</span>
          <strong class="small">Transformar estructura JSON</strong>
        </div>
        <div class="util-card-body">
          <p class="text-muted small mb-3">
            Transforma un archivo JSON aplanando su estructura de detalle para facilitar el procesamiento.
          </p>
          <div class="mb-3">
            <label class="form-label fw-bold small">Archivo JSON</label>
            <input
              type="file"
              class="form-control form-control-sm"
              ref="fileJsonInput"
              accept=".json"
              @change="archivoJson = $event.target.files[0]"
            />
          </div>
          <button class="btn btn-success" @click="transformarJson" :disabled="isLoadingJson || !archivoJson">
            <span class="material-symbols-outlined me-1" style="font-size:18px; vertical-align:middle;">
              {{ isLoadingJson ? 'hourglass_top' : 'download' }}
            </span>
            {{ isLoadingJson ? 'Procesando...' : 'Transformar y Descargar JSON' }}
          </button>
          <div v-if="errorMessageJson" class="alert alert-danger py-1 px-2 small mt-2 mb-0">
            <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">error</span>
            {{ errorMessageJson }}
          </div>
          <div v-if="successMessageJson" class="alert alert-success py-1 px-2 small mt-2 mb-0">
            <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">check_circle</span>
            {{ successMessageJson }}
          </div>
        </div>
      </div>
    </div>

    <!-- Tab: Transformar CSV -->
    <div v-show="tabActivo === 'transform-csv'">
      <div class="util-card border-warning">
        <div class="util-card-header bg-warning text-dark">
          <span class="material-symbols-outlined">table_convert</span>
          <strong class="small">Transformar CSV de respuestas</strong>
        </div>
        <div class="util-card-body">
          <p class="text-muted small mb-3">
            Transforma el archivo CSV de respuestas del financiador generando las columnas y cálculos requeridos.
          </p>
          <div class="mb-3">
            <label class="form-label fw-bold small">Archivo CSV</label>
            <input
              type="file"
              class="form-control form-control-sm"
              ref="fileCsvTransformInput"
              accept=".csv"
              @change="archivoCsvTransform = $event.target.files[0]"
            />
          </div>
          <button class="btn btn-warning" @click="transformarCsv" :disabled="isLoadingCsvTransform || !archivoCsvTransform">
            <span class="material-symbols-outlined me-1" style="font-size:18px; vertical-align:middle;">
              {{ isLoadingCsvTransform ? 'hourglass_top' : 'download' }}
            </span>
            {{ isLoadingCsvTransform ? 'Procesando...' : 'Transformar y Descargar CSV' }}
          </button>
          <div v-if="errorMessageCsvTransform" class="alert alert-danger py-1 px-2 small mt-2 mb-0">
            <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">error</span>
            {{ errorMessageCsvTransform }}
          </div>
          <div v-if="successMessageCsvTransform" class="alert alert-success py-1 px-2 small mt-2 mb-0">
            <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">check_circle</span>
            {{ successMessageCsvTransform }}
          </div>
        </div>
      </div>
    </div>

    <!-- Tab: Filtrar planillas JSON -->
    <div v-show="tabActivo === 'filtrar-json'">
      <div class="util-card border-danger">
        <div class="util-card-header bg-danger text-white">
          <span class="material-symbols-outlined">filter_alt</span>
          <strong class="small">Filtrar planillas del JSON</strong>
        </div>
        <div class="util-card-body">
          <p class="text-muted small mb-3">
            Elimina del JSON las planillas cuyos números se indiquen y descarga el archivo resultante.
          </p>
          <div class="mb-3">
            <label class="form-label fw-bold small">Archivo JSON</label>
            <input
              type="file"
              class="form-control form-control-sm"
              ref="fileJsonFiltrarInput"
              accept=".json"
              @change="archivoJsonFiltrar = $event.target.files[0]"
            />
          </div>
          <div class="mb-3">
            <label class="form-label fw-bold small">
              Números a eliminar
              <span class="text-muted fw-normal">(separados por coma o salto de línea)</span>
            </label>
            <textarea
              class="form-control form-control-sm"
              rows="3"
              v-model="numerosExcluir"
              placeholder="112043, 112151, 112167&#10;o uno por línea"
              style="font-family: monospace; font-size: 0.8rem;"
            ></textarea>
            <div class="text-muted" style="font-size:0.72rem; margin-top:3px;">
              {{ contadorNumerosExcluir }} número{{ contadorNumerosExcluir !== 1 ? 's' : '' }} ingresado{{ contadorNumerosExcluir !== 1 ? 's' : '' }}
            </div>
          </div>
          <button
            class="btn btn-danger"
            @click="filtrarPlanillasJson"
            :disabled="isLoadingJsonFiltrar || !archivoJsonFiltrar || !numerosExcluir.trim()"
          >
            <span class="material-symbols-outlined me-1" style="font-size:18px; vertical-align:middle;">
              {{ isLoadingJsonFiltrar ? 'hourglass_top' : 'download' }}
            </span>
            {{ isLoadingJsonFiltrar ? 'Procesando...' : 'Filtrar y Descargar JSON' }}
          </button>
          <div v-if="errorMessageJsonFiltrar" class="alert alert-danger py-1 px-2 small mt-2 mb-0">
            <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">error</span>
            {{ errorMessageJsonFiltrar }}
          </div>
          <div v-if="successMessageJsonFiltrar" class="alert alert-success py-1 px-2 small mt-2 mb-0">
            <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">check_circle</span>
            {{ successMessageJsonFiltrar }}
          </div>
        </div>
      </div>
    </div>

    <!-- Tab: Completar detalle -->
    <div v-show="tabActivo === 'completar'">
      <div class="util-card border-purple">
        <div class="util-card-header text-white" style="background:#6f42c1;">
          <span class="material-symbols-outlined">playlist_add</span>
          <strong class="small">Completar detalle nulo</strong>
        </div>
        <div class="util-card-body">
          <p class="text-muted small mb-3">
            Para cada planilla con <code>detalle: null</code> inyecta un registro de detalle fijo,
            usando <code>fechaDesde</code> como <code>fechaAtencion</code>. Las planillas que ya tienen detalle no se modifican.
          </p>
          <div class="row g-3 mb-3">
            <div class="col-12">
              <label class="form-label fw-bold small">Archivo JSON</label>
              <input
                type="file"
                class="form-control form-control-sm"
                ref="fileCompletarInput"
                accept=".json"
                @change="archivoCompletar = $event.target.files[0]"
              />
            </div>
          </div>
          <p class="fw-bold small mb-2">Datos del detalle a inyectar</p>
          <div class="row g-2 mb-3">
            <div class="col-md-3">
              <label class="form-label small">idElemento</label>
              <input type="text" class="form-control form-control-sm" v-model="detalleItem.idElemento" />
            </div>
            <div class="col-md-3">
              <label class="form-label small">Código</label>
              <input type="text" class="form-control form-control-sm" v-model="detalleItem.codigo" />
            </div>
            <div class="col-md-2">
              <label class="form-label small">Cantidad</label>
              <input type="text" class="form-control form-control-sm" v-model="detalleItem.cantidad" />
            </div>
            <div class="col-md-2">
              <label class="form-label small">Valor unitario</label>
              <input type="text" class="form-control form-control-sm" v-model="detalleItem.valorUni" />
            </div>
            <div class="col-md-12">
              <label class="form-label small">Descripción</label>
              <input type="text" class="form-control form-control-sm" v-model="detalleItem.descripcion" />
            </div>
          </div>
          <button
            class="btn btn-purple"
            style="background:#6f42c1; color:#fff; border-color:#6f42c1;"
            @click="completarDetalle"
            :disabled="isLoadingCompletar || !archivoCompletar"
          >
            <span class="material-symbols-outlined me-1" style="font-size:18px; vertical-align:middle;">
              {{ isLoadingCompletar ? 'hourglass_top' : 'playlist_add' }}
            </span>
            {{ isLoadingCompletar ? 'Procesando...' : 'Completar y Descargar JSON' }}
          </button>
          <div v-if="errorMessageCompletar" class="alert alert-danger py-1 px-2 small mt-2 mb-0">
            <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">error</span>
            {{ errorMessageCompletar }}
          </div>
          <div v-if="successMessageCompletar" class="alert alert-success py-1 px-2 small mt-2 mb-0">
            <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">check_circle</span>
            {{ successMessageCompletar }}
          </div>
        </div>
      </div>
    </div>

    <!-- Tab: Fusionar JSON -->
    <div v-show="tabActivo === 'fusionar'">
      <div class="util-card border-dark">
        <div class="util-card-header bg-dark text-white">
          <span class="material-symbols-outlined">merge</span>
          <strong class="small">Fusionar archivos JSON</strong>
        </div>
        <div class="util-card-body">
          <p class="text-muted small mb-3">
            Combina varios archivos JSON de planillas en uno solo, ordenado por número de planilla.
          </p>
          <div class="mb-3">
            <label class="form-label fw-bold small">
              Archivos JSON
              <span class="text-muted fw-normal">(selecciona múltiples con Ctrl+Click)</span>
            </label>
            <input
              type="file"
              class="form-control form-control-sm"
              ref="filesFusionarInput"
              accept=".json"
              multiple
              @change="archivosFusionar = Array.from($event.target.files)"
            />
            <div v-if="archivosFusionar.length" class="mt-2">
              <div
                v-for="(f, i) in archivosFusionar"
                :key="i"
                class="d-flex align-items-center gap-1 text-muted small"
              >
                <span class="material-symbols-outlined" style="font-size:14px">draft</span>
                {{ f.name }}
              </div>
            </div>
          </div>
          <button
            class="btn btn-dark"
            @click="fusionarJsons"
            :disabled="isLoadingFusionar || archivosFusionar.length < 2"
          >
            <span class="material-symbols-outlined me-1" style="font-size:18px; vertical-align:middle;">
              {{ isLoadingFusionar ? 'hourglass_top' : 'merge' }}
            </span>
            {{ isLoadingFusionar ? 'Procesando...' : 'Fusionar y Descargar JSON' }}
          </button>
          <span v-if="archivosFusionar.length === 1" class="text-warning small ms-2">
            Selecciona al menos 2 archivos
          </span>
          <div v-if="errorMessageFusionar" class="alert alert-danger py-1 px-2 small mt-2 mb-0">
            <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">error</span>
            {{ errorMessageFusionar }}
          </div>
          <div v-if="successMessageFusionar" class="alert alert-success py-1 px-2 small mt-2 mb-0">
            <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">check_circle</span>
            {{ successMessageFusionar }}
          </div>
        </div>
      </div>
    </div>

    <!-- Tab: JSON ↔ CSV -->
    <div v-show="tabActivo === 'json-csv'">
      <div class="util-card border-secondary">
        <div class="util-card-header bg-secondary text-white">
          <span class="material-symbols-outlined">table_rows</span>
          <strong class="small">Convertir JSON ↔ CSV</strong>
        </div>
        <div class="util-card-body">
          <div class="row g-4">

            <!-- Columna izquierda: JSON → CSV -->
            <div class="col-md-6">
              <div class="conv-panel border-start border-4 border-secondary ps-3">
                <div class="conv-panel-title mb-2">
                  <span class="material-symbols-outlined conv-icon text-secondary">arrow_downward</span>
                  <span class="fw-bold small">JSON → CSV</span>
                </div>
                <p class="text-muted small mb-3">
                  Convierte un archivo JSON de planillas a CSV separado por
                  <code>;</code>, listo para abrir en Excel.
                </p>
                <div class="mb-3">
                  <label class="form-label fw-bold small">Archivo JSON</label>
                  <input
                    type="file"
                    class="form-control form-control-sm"
                    ref="fileJsonCsvInput"
                    accept=".json"
                    @change="archivoJsonCsv = $event.target.files[0]"
                  />
                </div>
                <button
                  class="btn btn-secondary btn-sm"
                  @click="convertirJsonACsv"
                  :disabled="isLoadingJsonCsv || !archivoJsonCsv"
                >
                  <span class="material-symbols-outlined me-1" style="font-size:16px;vertical-align:middle;">
                    {{ isLoadingJsonCsv ? 'hourglass_top' : 'download' }}
                  </span>
                  {{ isLoadingJsonCsv ? 'Procesando...' : 'Convertir y Descargar CSV' }}
                </button>
                <div v-if="errorMessageJsonCsv" class="alert alert-danger py-1 px-2 small mt-2 mb-0">
                  <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">error</span>
                  {{ errorMessageJsonCsv }}
                </div>
                <div v-if="successMessageJsonCsv" class="alert alert-success py-1 px-2 small mt-2 mb-0">
                  <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">check_circle</span>
                  {{ successMessageJsonCsv }}
                </div>
              </div>
            </div>

            <!-- Divisor vertical -->
<!--			
            <div class="col-md-auto d-none d-md-flex align-items-stretch px-0">
              <div class="conv-divider"></div>
            </div>
-->

            <!-- Columna derecha: CSV → JSON -->
            <div class="col-md-6">
              <div class="conv-panel border-start border-4 border-info ps-3">
                <div class="conv-panel-title mb-2">
                  <span class="material-symbols-outlined conv-icon text-info">arrow_upward</span>
                  <span class="fw-bold small">CSV → JSON</span>
                </div>
                <p class="text-muted small mb-3">
                  Convierte el CSV generado anteriormente de vuelta a formato JSON,
                  reconstruyendo la estructura original de planillas.
                </p>
                <div class="mb-3">
                  <label class="form-label fw-bold small">Archivo CSV</label>
                  <input
                    type="file"
                    class="form-control form-control-sm"
                    ref="fileCsvJsonInput"
                    accept=".csv"
                    @change="archivoCsvJson = $event.target.files[0]"
                  />
                </div>
                <button
                  class="btn btn-info btn-sm text-white"
                  @click="convertirCsvAJson"
                  :disabled="isLoadingCsvJson || !archivoCsvJson"
                >
                  <span class="material-symbols-outlined me-1" style="font-size:16px;vertical-align:middle;">
                    {{ isLoadingCsvJson ? 'hourglass_top' : 'download' }}
                  </span>
                  {{ isLoadingCsvJson ? 'Procesando...' : 'Convertir y Descargar JSON' }}
                </button>
                <div v-if="errorMessageCsvJson" class="alert alert-danger py-1 px-2 small mt-2 mb-0">
                  <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">error</span>
                  {{ errorMessageCsvJson }}
                </div>
                <div v-if="successMessageCsvJson" class="alert alert-success py-1 px-2 small mt-2 mb-0">
                  <span class="material-symbols-outlined me-1" style="font-size:14px;vertical-align:middle;">check_circle</span>
                  {{ successMessageCsvJson }}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import api from "@/services/api";

export default {
  name: "TramiteUtilidades",

  props: {
    idTramite: { type: Number, required: true },
    idUsuario: { type: Number, required: true },
  },

  data() {
    return {
      // Tabs
      tabActivo: "csv-json",
      tabs: [
        { id: "csv-json",       label: "CSV → JSON",       icono: "upload_file"   },
        { id: "transform-json", label: "Transformar JSON",  icono: "data_object"   },
        { id: "transform-csv",  label: "Transformar CSV",   icono: "table_convert" },
        { id: "filtrar-json",   label: "Filtrar planillas", icono: "filter_alt"    },
        { id: "json-csv",       label: "JSON ↔ CSV",         icono: "table_rows"    },
        { id: "fusionar",       label: "Fusionar JSON",      icono: "merge"         },
        { id: "completar",      label: "Completar detalle",  icono: "playlist_add"  },
      ],

      // CSV → JSON
      file:           null,
      isLoading:      false,
      errorMessage:   "",
      successMessage: "",

      // Transformar JSON
      archivoJson:          null,
      isLoadingJson:        false,
      errorMessageJson:     "",
      successMessageJson:   "",

      // Transformar CSV
      archivoCsvTransform:        null,
      isLoadingCsvTransform:      false,
      errorMessageCsvTransform:   null,
      successMessageCsvTransform: null,

      // Filtrar planillas JSON
      archivoJsonFiltrar:         null,
      numerosExcluir:             "",
      isLoadingJsonFiltrar:       false,
      errorMessageJsonFiltrar:    null,
      successMessageJsonFiltrar:  null,

      // Completar detalle
      archivoCompletar:           null,
      detalleItem: {
        idElemento:  "17136",
        codigo:      "291146",
        descripcion: "TERAPIA DEL LENGUAJE, SESIÓN  DE 50 MINUTOS (VARIOS PROCEDIMIENTOS TERAPÉUTICOS)",
        cantidad:    "1",
        valorUni:    "8.26",
      },
      isLoadingCompletar:         false,
      errorMessageCompletar:      null,
      successMessageCompletar:    null,

      // Fusionar JSON
      archivosFusionar:           [],
      isLoadingFusionar:          false,
      errorMessageFusionar:       null,
      successMessageFusionar:     null,

      // JSON → CSV
      archivoJsonCsv:           null,
      isLoadingJsonCsv:         false,
      errorMessageJsonCsv:      null,
      successMessageJsonCsv:    null,

      // CSV → JSON (inverso)
      archivoCsvJson:           null,
      isLoadingCsvJson:         false,
      errorMessageCsvJson:      null,
      successMessageCsvJson:    null,
    };
  },

  computed: {
    contadorNumerosExcluir() {
      return this.numerosExcluir
        .split(/[\n,]+/)
        .map((n) => n.trim())
        .filter(Boolean).length;
    },
  },

  methods: {
    // ── CSV → JSON ──────────────────────────────────────────────
    async handleSubmit() {
      this.errorMessage   = "";
      this.successMessage = "";
      if (!this.idTramite || !this.idUsuario || !this.file) {
        this.errorMessage = "Por favor, complete todos los campos.";
        return;
      }
      this.isLoading = true;
      try {
        const formData = new FormData();
        formData.append("idTramite", this.idTramite);
        formData.append("idUsuario", this.idUsuario);
        formData.append("file", this.file);
        const response = await api.post("/cargaplanos3/csv-to-json", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        });
        const nombre = this.file.name.replace(/\.csv$/i, "") + ".json";
        this.descargar(response.data, nombre);
        this.successMessage = "Archivo CSV procesado y JSON descargado exitosamente.";
        this.file = null;
        this.$refs.fileInput.value = "";
      } catch (error) {
        this.errorMessage = await this.leerError(error);
      } finally {
        this.isLoading = false;
      }
    },

    // ── Transformar JSON ────────────────────────────────────────
    async transformarJson() {
      this.errorMessageJson   = "";
      this.successMessageJson = "";
      if (!this.archivoJson) {
        this.errorMessageJson = "Por favor, seleccione un archivo JSON.";
        return;
      }
      this.isLoadingJson = true;
      try {
        const formData = new FormData();
        formData.append("file", this.archivoJson);
        const response = await api.post("/json/transformar", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        });
        this.descargar(response.data, this.archivoJson.name.replace(/\.json$/i, "") + "_OK.json");
        this.successMessageJson = "Archivo transformado y descargado correctamente.";
        this.archivoJson = null;
        this.$refs.fileJsonInput.value = "";
      } catch (error) {
        this.errorMessageJson = await this.leerError(error);
      } finally {
        this.isLoadingJson = false;
      }
    },

    // ── Transformar CSV ─────────────────────────────────────────
    async transformarCsv() {
      this.errorMessageCsvTransform   = null;
      this.successMessageCsvTransform = null;
      if (!this.archivoCsvTransform) return;
      this.isLoadingCsvTransform = true;
      const orig   = this.archivoCsvTransform.name;
      const nombre = orig.includes(".")
        ? orig.substring(0, orig.lastIndexOf(".")) + "_nuevo_OK" + orig.substring(orig.lastIndexOf("."))
        : orig + "_formato.csv";
      try {
        const formData = new FormData();
        formData.append("file", this.archivoCsvTransform);
        const response = await api.post("/cargaplanos3/csv-transformar-v2", formData, {
          responseType: "blob",
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.descargar(response.data, nombre);
        this.successMessageCsvTransform = "Archivo transformado y descargado exitosamente.";
        this.archivoCsvTransform = null;
        this.$refs.fileCsvTransformInput.value = "";
      } catch (error) {
        this.errorMessageCsvTransform = await this.leerError(error);
      } finally {
        this.isLoadingCsvTransform = false;
      }
    },

    // ── Filtrar planillas JSON ───────────────────────────────────
    async filtrarPlanillasJson() {
      this.errorMessageJsonFiltrar   = null;
      this.successMessageJsonFiltrar = null;
      if (!this.archivoJsonFiltrar || !this.numerosExcluir.trim()) return;
      this.isLoadingJsonFiltrar = true;
      const numeros = this.numerosExcluir.split(/[\n,]+/).map((n) => n.trim()).filter(Boolean);
      try {
        const formData = new FormData();
        formData.append("archivo", this.archivoJsonFiltrar);
        numeros.forEach((n) => formData.append("excluir", n));
        const response = await api.post("/planillas/filtrar", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        });
        this.descargar(response.data, this.archivoJsonFiltrar.name.replace(/\.json$/i, "") + "_filtrado.json");
        this.successMessageJsonFiltrar = `${numeros.length} planilla${numeros.length !== 1 ? "s" : ""} eliminada${numeros.length !== 1 ? "s" : ""}. Archivo descargado.`;
        this.archivoJsonFiltrar = null;
        this.numerosExcluir     = "";
        this.$refs.fileJsonFiltrarInput.value = "";
      } catch (error) {
        this.errorMessageJsonFiltrar = await this.leerError(error);
      } finally {
        this.isLoadingJsonFiltrar = false;
      }
    },

    // ── JSON → CSV ──────────────────────────────────────────────
    async convertirJsonACsv() {
      this.errorMessageJsonCsv   = null;
      this.successMessageJsonCsv = null;
      if (!this.archivoJsonCsv) return;
      this.isLoadingJsonCsv = true;
      try {
        const formData = new FormData();
        formData.append("archivo", this.archivoJsonCsv);
        const response = await api.post("/planillas/json-a-csv", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        });
        const nombre = this.archivoJsonCsv.name.replace(/\.json$/i, "") + ".csv";
        this.descargar(response.data, nombre);
        this.successMessageJsonCsv = "Archivo CSV generado y descargado correctamente.";
        this.archivoJsonCsv = null;
        this.$refs.fileJsonCsvInput.value = "";
      } catch (error) {
        this.errorMessageJsonCsv = await this.leerError(error);
      } finally {
        this.isLoadingJsonCsv = false;
      }
    },

    // ── Completar detalle nulo ──────────────────────────────────
    async completarDetalle() {
      this.errorMessageCompletar   = null;
      this.successMessageCompletar = null;
      if (!this.archivoCompletar) return;
      this.isLoadingCompletar = true;
      try {
        const formData = new FormData();
        formData.append("archivo",      this.archivoCompletar);
        formData.append("idElemento",   this.detalleItem.idElemento);
        formData.append("codigo",       this.detalleItem.codigo);
        formData.append("descripcion",  this.detalleItem.descripcion);
        formData.append("cantidad",     this.detalleItem.cantidad);
        formData.append("valorUni",     this.detalleItem.valorUni);
        const response = await api.post("/planillas/completar-detalle", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        });
        // El backend devuelve en el header cuántos registros modificó
        const modificadas = response.headers["x-registros-modificados"] || "?";
        const nombre = this.archivoCompletar.name.replace(/\.json$/i, "") + "_detalle.json";
        this.descargar(response.data, nombre);
        this.successMessageCompletar =
          `${modificadas} planilla(s) completadas con detalle. Archivo descargado.`;
        this.archivoCompletar = null;
        this.$refs.fileCompletarInput.value = "";
      } catch (error) {
        this.errorMessageCompletar = await this.leerError(error);
      } finally {
        this.isLoadingCompletar = false;
      }
    },

    // ── Fusionar JSONs ──────────────────────────────────────────
    async fusionarJsons() {
      this.errorMessageFusionar   = null;
      this.successMessageFusionar = null;
      if (!this.archivosFusionar.length) return;
      this.isLoadingFusionar = true;
      try {
        const formData = new FormData();
        this.archivosFusionar.forEach((f) => formData.append("archivos", f));
        const response = await api.post("/planillas/fusionar", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        });
        this.descargar(response.data, "planillas_fusionadas.json");
        this.successMessageFusionar =
          `${this.archivosFusionar.length} archivos fusionados y descargados correctamente.`;
        this.archivosFusionar = [];
        this.$refs.filesFusionarInput.value = "";
      } catch (error) {
        this.errorMessageFusionar = await this.leerError(error);
      } finally {
        this.isLoadingFusionar = false;
      }
    },

    // ── CSV → JSON (inverso) ────────────────────────────────────
    async convertirCsvAJson() {
      this.errorMessageCsvJson   = null;
      this.successMessageCsvJson = null;
      if (!this.archivoCsvJson) return;
      this.isLoadingCsvJson = true;
      try {
        const formData = new FormData();
        formData.append("archivo", this.archivoCsvJson);
        const response = await api.post("/planillas/csv-a-json", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        });
        const nombre = this.archivoCsvJson.name.replace(/\.csv$/i, "") + ".json";
        this.descargar(response.data, nombre);
        this.successMessageCsvJson = "Archivo JSON generado y descargado correctamente.";
        this.archivoCsvJson = null;
        this.$refs.fileCsvJsonInput.value = "";
      } catch (error) {
        this.errorMessageCsvJson = await this.leerError(error);
      } finally {
        this.isLoadingCsvJson = false;
      }
    },

    // ── Helpers ─────────────────────────────────────────────────
    descargar(data, nombreArchivo) {
      const url  = URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href     = url;
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    async leerError(error) {
      if (error.response?.data?.text) {
        const text = await error.response.data.text();
        return text || "Error al procesar la solicitud.";
      }
      return error.message || "Error al procesar la solicitud.";
    },
  },
};
</script>

<style scoped>
.nav-tabs .nav-link               { color: #495057; border-radius: 6px 6px 0 0; }
.nav-tabs .nav-link.active        { font-weight: 600; }
.nav-tabs .nav-link:hover:not(.active) { background: #f8f9fa; }

.util-card         { border: 1px solid; border-radius: 8px; overflow: hidden; }
.util-card-header  { display: flex; align-items: center; gap: 8px; padding: 10px 16px; }
.util-card-body    { padding: 20px; }

.conv-panel        { padding-bottom: 4px; }
.conv-panel-title  { display: flex; align-items: center; gap: 6px; }
.conv-icon         { font-size: 18px !important; }
.conv-divider      { width: 1px; background: #dee2e6; margin: 0 8px; }
</style>